import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import { useTheme } from "@/shared/context/ThemeContext";
import RouteMap from "@/shared/components/RouteMap";
import {
  Loader2, Phone, MessageSquare, Power, ChevronLeft, ChevronRight,
  CheckCircle2, Calendar, Navigation, Menu, X, Circle, MapPin, User,
  Sun, Moon, Monitor, Compass, Settings as SettingsIcon, Lock, Eye, EyeOff,
  TrendingUp, Map as MapIcon, Layers, Clock,
} from "lucide-react";
import { formatTime12 } from "@/shared/lib/format";
import { toast } from "sonner";
import { SidePanel } from "@/shared/motion";

/**
 * /driver — gesture-based bottom-sheet driver dashboard.
 *
 * Layout:
 *   · Full-bleed map (driver marker, route, pickup/drop pins only)
 *   · Top-right hamburger menu
 *   · Bottom sheet (collapsed | mid | expanded) with drag-to-resize
 *
 *  Sheet header:
 *     ← Today, May 2 →            (date arrows + tap-to-open calendar modal)
 *
 *  Collapsed (default):
 *     "UP NEXT" pill · rider name · pickup time · pickup address · primary action
 *
 *  Mid:
 *     Same Up-Next card + peek list of next 2 upcoming rides
 *
 *  Expanded:
 *     Scrollable grouped session list, completed hidden by default + toggle.
 */

const POLL_MS = 8000;

const NEXT = {
  scheduled:        { label: "On My Way",     next: "driver_en_route" },
  driver_assigned:  { label: "On My Way",     next: "driver_en_route" },
  driver_en_route:  { label: "Arrived",       next: "arriving_soon"   },
  arriving_soon:    { label: "Start Trip",    next: "in_progress"     },
  in_progress:      { label: "Complete Ride", next: "completed"       },
};

const STATUS_LABEL = {
  scheduled:       "Ready",
  driver_assigned: "Ready",
  driver_en_route: "En route",
  arriving_soon:   "Arrived",
  in_progress:     "In ride",
  completed:       "Done",
  cancelled:       "Cancelled",
};

const STATUS_DOT = {
  scheduled:       "bg-white/35",
  driver_assigned: "bg-white/35",
  driver_en_route: "bg-[#5c97f7] animate-pulse",
  arriving_soon:   "bg-[#ed6500] animate-pulse",
  in_progress:     "bg-[#ed6500]",
  completed:       "bg-emerald-400",
  cancelled:       "bg-white/30",
};

const ACTIVE_STATES = new Set(["driver_en_route", "arriving_soon", "in_progress"]);

// Sheet snap points
const SNAP_COLLAPSED_PX = 312; // handle (~72) + header (~70) + up-next card (~170)
function getMidPx()      { return Math.round(window.innerHeight * 0.55); }
function getExpandedPx() { return Math.round(window.innerHeight * 0.88); }

/* ===========================================================
   Helpers
   =========================================================== */
function minutesUntil(dateIso, hhmm, nowMs) {
  if (!dateIso || !hhmm) return null;
  const [hh, mm] = hhmm.split(":").map(Number);
  const [y, m, d] = dateIso.split("-").map(Number);
  const target = new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
  return Math.round((target - nowMs) / 60000);
}

function relativeTimeLabel(mins) {
  if (mins == null) return "";
  if (mins < -120) return `${Math.round(-mins / 60)} h ago`;
  if (mins < -1) return `${-mins} min ago`;
  if (mins <= 1) return "now";
  if (mins < 60) return `in ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `in ${h} h` : `in ${h} h ${m} min`;
}

function dateLongLabel(iso, todayIso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const today = new Date(todayIso + "T00:00:00");
  const dayDiff = Math.round((dt - today) / 86400000);
  const monthDay = dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  if (dayDiff === 0)   return `Today, ${monthDay}`;
  if (dayDiff === 1)   return `Tomorrow, ${monthDay}`;
  if (dayDiff === -1)  return `Yesterday, ${monthDay}`;
  const weekday = dt.toLocaleDateString(undefined, { weekday: "short" });
  return `${weekday}, ${monthDay}`;
}

function googleMapsDeeplink(address) {
  if (!address) return "#";
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

/**
 * Sector label lookup — a coarse, deterministic mapping from
 * lat/lng buckets to KC-area neighborhood names. Intentionally rough:
 * the goal is to give drivers a sense of where they are operationally
 * without us having to hit a paid reverse-geocode every position
 * update. When we eventually back this with real zone polygons +
 * demand, this function becomes the single replacement point.
 */
const SECTOR_DEFINITIONS = [
  // [name, lat_min, lat_max, lng_min, lng_max]
  ["Downtown KC",  39.085, 39.115, -94.605, -94.560],
  ["North KC",     39.115, 39.180, -94.620, -94.530],
  ["Plaza",        39.030, 39.060, -94.605, -94.575],
  ["Westport",     39.055, 39.080, -94.605, -94.580],
  ["Independence", 39.060, 39.130, -94.470, -94.380],
  ["Overland Park",38.950, 39.020, -94.700, -94.620],
  ["Airport",      39.275, 39.330, -94.730, -94.680],
];
function sectorForCoords(lat, lng) {
  for (const [name, latMin, latMax, lngMin, lngMax] of SECTOR_DEFINITIONS) {
    if (lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax) return name;
  }
  return "Kansas City";
}

/* ===========================================================
   Group flat legs into rider sessions.  A "session" = the same rider's
   outbound + return on the same operating day (overnight returns folded
   back into prior calendar day's session via route_id + date-1 lookup).
   =========================================================== */
function groupIntoSessions(legs) {
  const byRoute = new Map();
  for (const leg of legs) {
    const key = `${leg.route_id}::${leg.date}`;
    let foldKey = key;
    if (leg.direction === "return") {
      const prevDate = (() => {
        try {
          const [y, m, d] = leg.date.split("-").map(Number);
          const dt = new Date(y, m - 1, d - 1);
          const yyyy = dt.getFullYear();
          const mm = String(dt.getMonth() + 1).padStart(2, "0");
          const dd = String(dt.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        } catch { return null; }
      })();
      const prevKey = prevDate ? `${leg.route_id}::${prevDate}` : null;
      if (prevKey && byRoute.has(prevKey) && !byRoute.get(prevKey).return) {
        foldKey = prevKey;
      }
    }
    if (!byRoute.has(foldKey)) {
      byRoute.set(foldKey, {
        id: foldKey,
        route_id: leg.route_id,
        date: leg.date,
        rider_id: leg.user_id,
        rider_name: leg.rider_name || "Rider",
        rider_phone: leg.rider_phone,
        rider_photo: leg.rider_photo,
        tier_name: leg.tier_name,
        distance_miles: leg.distance_miles,
        outbound: null,
        return: null,
      });
    }
    const s = byRoute.get(foldKey);
    if (leg.direction === "return") s.return = leg;
    else s.outbound = leg;
  }
  const list = Array.from(byRoute.values()).map((s) => {
    const earliest = s.outbound || s.return;
    s._sort = `${earliest?.date || s.date}T${earliest?.pickup_time || "00:00"}`;
    return s;
  });
  list.sort((a, b) => a._sort.localeCompare(b._sort));
  return list;
}

/* ===========================================================
   Root
   =========================================================== */
export default function DriverApp() {
  const { user, loading: authLoading, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [legs, setLegs] = useState([]);
  const [dates, setDates] = useState([]);
  const [today, setToday] = useState(new Date().toISOString().slice(0, 10));
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [pos, setPos] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [actioning, setActioning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeDock, setActiveDock] = useState("map"); // "map" | "blocks" | "account"
  const watchIdRef = useRef(null);
  const lastPushedAtRef = useRef(0);

  // Theme-aware iOS PWA status-bar tint.  iOS PWA in standalone mode
  // honors `theme-color` based on the OS prefers-color-scheme; we update
  // BOTH meta tags so the chosen tint matches the app's resolved theme
  // regardless of OS appearance.
  const { theme } = useTheme();
  useEffect(() => {
    const tint = theme === "dark" ? "#0f172a" : "#F9F7F4";
    const metas = document.querySelectorAll('meta[name="theme-color"]');
    const prev = Array.from(metas).map((m) => m.getAttribute("content"));
    metas.forEach((m) => m.setAttribute("content", tint));
    return () => {
      metas.forEach((m, i) => { if (prev[i] != null) m.setAttribute("content", prev[i]); });
    };
  }, [theme]);

  // Auth gate
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      toast.info("Your session has ended — please sign in to continue driving.");
      nav("/login", { replace: true });
      return;
    }
    if (!user.is_driver) nav("/account", { replace: true });
  }, [user, authLoading, nav]);

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/me/schedule", { params: { days: 14 } });
      setLegs(data.legs || []);
      setDates(data.dates || []);
      if (data.today) setToday(data.today);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user?.is_driver) return;
    fetchSchedule();
    const id = setInterval(fetchSchedule, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchSchedule]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Geolocation watch with heading
  useEffect(() => {
    if (!user?.is_driver) return;
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (p) => setPos({
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        heading: Number.isFinite(p.coords.heading) ? p.coords.heading : null,
      }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 },
    );
    watchIdRef.current = id;
    return () => { try { navigator.geolocation.clearWatch(id); } catch (_) {} };
  }, [user]);

  // Sessions for selected date (filtered for completed unless toggled on)
  const allSessions = useMemo(() => groupIntoSessions(legs), [legs]);
  const sessionsForDate = useMemo(() => {
    return allSessions.filter((s) => {
      const targetDate = s.outbound?.date || s.return?.date || s.date;
      return targetDate === selectedDate;
    });
  }, [allSessions, selectedDate]);

  const visibleSessions = useMemo(() => {
    if (showCompleted) return sessionsForDate;
    return sessionsForDate.filter((s) => {
      const legs = [s.outbound, s.return].filter(Boolean);
      return !(legs.length > 0 && legs.every((l) => l.status === "completed"));
    });
  }, [sessionsForDate, showCompleted]);

  const completedCount = sessionsForDate.length - visibleSessions.length;

  // Active session = first live or earliest non-completed in visibleSessions
  const activeSession = useMemo(() => {
    const list = visibleSessions;
    if (activeSessionId) {
      const found = list.find((s) => s.id === activeSessionId);
      if (found) return found;
    }
    const live = list.find((s) =>
      ACTIVE_STATES.has(s.outbound?.status) || ACTIVE_STATES.has(s.return?.status)
    );
    if (live) return live;
    const next = list.find((s) =>
      (s.outbound && !["completed", "cancelled"].includes(s.outbound.status)) ||
      (s.return && !["completed", "cancelled"].includes(s.return.status))
    );
    return next || list[0] || null;
  }, [visibleSessions, activeSessionId]);

  const activeLeg = useMemo(() => {
    if (!activeSession) return null;
    const ob = activeSession.outbound;
    const rb = activeSession.return;
    if (ob && ACTIVE_STATES.has(ob.status)) return ob;
    if (rb && ACTIVE_STATES.has(rb.status)) return rb;
    if (ob && !["completed", "cancelled"].includes(ob.status)) return ob;
    if (rb && !["completed", "cancelled"].includes(rb.status)) return rb;
    return ob || rb;
  }, [activeSession]);

  // Push location for active leg
  useEffect(() => {
    if (!pos || !activeLeg) return;
    if (!ACTIVE_STATES.has(activeLeg.status)) return;
    const t = Date.now();
    if (t - lastPushedAtRef.current < POLL_MS - 200) return;
    lastPushedAtRef.current = t;
    api.post("/driver/location", { trip_id: activeLeg.id, lat: pos.lat, lng: pos.lng }).catch(() => {});
  }, [pos, activeLeg]);

  const mapCenter = useMemo(() => {
    if (pos) return pos;
    const stage = activeLeg && ["arriving_soon", "in_progress"].includes(activeLeg.status)
      ? "destination" : "pickup";
    if (stage === "destination" && activeLeg?.destination_coords) return activeLeg.destination_coords;
    if (activeLeg?.pickup_coords) return activeLeg.pickup_coords;
    return { lat: 39.0997, lng: -94.5786 };
  }, [pos, activeLeg]);

  const mapPolyline = activeLeg?.encoded_polyline || "";

  const driverMarkerState = useMemo(() => {
    const s = activeLeg?.status;
    if (s === "in_progress") return "on_trip";
    if (s === "driver_en_route" || s === "arriving_soon") return "en_route";
    return "idle";
  }, [activeLeg]);

  // Per-leg LIVE ETA from the active driver position to the next relevant
  // address (pickup pre-arrival, dropoff once en-route to drop).  Backend
  // caches for ~30s so the 30s frontend poll keeps cost low. When pos is
  // unavailable we silently rely on the in-component static fallback (the
  // leg's stored route duration) — the UI always shows SOMETHING when an
  // ETA is meaningful.
  const [activeEta, setActiveEta] = useState(null); // {eta_minutes, distance_miles, source}
  useEffect(() => {
    if (!activeLeg || !pos) { setActiveEta(null); return; }
    if (["completed", "cancelled"].includes(activeLeg.status)) { setActiveEta(null); return; }
    const stage = ["arriving_soon", "in_progress"].includes(activeLeg.status) ? "dropoff" : "pickup";
    let cancelled = false;
    const fetchEta = async () => {
      try {
        const { data } = await api.get("/driver/me/eta", {
          params: { trip_id: activeLeg.id, lat: pos.lat, lng: pos.lng, stage },
        });
        if (!cancelled) setActiveEta(data);
      } catch { /* ignore */ }
    };
    fetchEta();
    const id = setInterval(fetchEta, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeLeg?.id, activeLeg?.status, pos?.lat, pos?.lng]);

  const advance = async () => {
    if (!activeLeg || actioning) return;
    const action = NEXT[activeLeg.status];
    if (!action) return;
    setActioning(true);
    try {
      await api.post(`/driver/trips/${activeLeg.id}/status`, { status: action.next });
      const NOTIFY = {
        driver_en_route: "Rider notified — you're on the way",
        arriving_soon:   "Rider notified — you've arrived",
        in_progress:     "Ride started",
        completed:       "Ride complete",
      };
      toast.success(NOTIFY[action.next] || STATUS_LABEL[action.next]);
      fetchSchedule();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update status");
    } finally {
      setActioning(false);
    }
  };

  const onLogout = async () => { try { await logout(); nav("/login"); } catch (_) {} };

  const onToggleOnline = async () => {
    const target = !(user?.is_online ?? true);
    try {
      await api.post("/driver/me/online", { is_online: target });
      await refreshUser?.();
      toast.success(target ? "You're online — accepting new rides" : "You're offline — paused for new rides");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update online status");
    }
  };

  const onShiftDate = (deltaDays) => {
    const cur = new Date(selectedDate + "T00:00:00");
    cur.setDate(cur.getDate() + deltaDays);
    const yyyy = cur.getFullYear();
    const mm = String(cur.getMonth() + 1).padStart(2, "0");
    const dd = String(cur.getDate()).padStart(2, "0");
    const next = `${yyyy}-${mm}-${dd}`;
    if (dates.includes(next)) {
      setSelectedDate(next);
      setActiveSessionId(null);
    }
  };

  const canPrev = dates.length > 0 && dates.indexOf(selectedDate) > 0;
  const canNext = dates.length > 0 && dates.indexOf(selectedDate) < dates.length - 1;

  // Operational summary derived from today's session list — drives the
  // floating "today's progress" pill. We avoid fabricating $$ figures
  // without a real payout endpoint (out of scope for this iteration);
  // ride count is the truthful operational signal.
  const completedToday = useMemo(() => {
    return sessionsForDate.filter((s) => {
      const legs = [s.outbound, s.return].filter(Boolean);
      return legs.length > 0 && legs.every((l) => l.status === "completed");
    }).length;
  }, [sessionsForDate]);

  // Sector label — a friendly KC neighborhood string. Until we wire up
  // a real reverse-geocode + zone polygon API we keep this deterministic
  // (city default + simple lat/lng bucket) so the pill never flickers.
  const sectorLabel = useMemo(() => {
    if (!pos) return "Kansas City";
    return sectorForCoords(pos.lat, pos.lng);
  }, [pos]);

  if (authLoading || loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#0A1128] text-white/60" data-testid="driver-loading">
        <Loader2 size={20} className="animate-spin text-[#ed6500]" />
      </div>
    );
  }
  if (!user?.is_driver) return null;

  const isOnline = user?.is_online ?? true;

  return (
    <div
      className="text-white"
      style={{
        // Wrapper is INTENTIONALLY transparent — the map renders behind
        // it edge-to-edge. A `fade-top` gradient overlay handles status-
        // bar legibility (matches rider /schedule pattern).  Without
        // this, painting a solid bg color creates a hard cream/navy
        // stripe in the iOS safe-area zone.
        position: "fixed", inset: 0,
        overflow: "hidden",
      }}
      data-testid="driver-app"
    >
      {/* Map base */}
      <div className="fixed inset-0" style={{ zIndex: 0 }}>
        <RouteMap
          center={mapCenter}
          polyline={mapPolyline}
          userLocation={pos}
          userHeading={pos?.heading}
          userMarkerVariant="driver"
          userMarkerState={driverMarkerState}
          fallbackSrc=""
          hideRecenter
        />
      </div>

      {/* Sector overlay chips — soft floating "operational zones" pinned
          across the map. Purely decorative right now (no demand API),
          but they give the surface that "alive city operations" feel
          while we wire up real demand data. Auto-rotate intensity over
          a slow timer so the city feels like it's breathing. */}
      <SectorOverlay now={now} />

      {/* Theme-aware top fade — covers the safe-area zone with a soft
          gradient that fades into the map.  No hard line, no stripe. */}
      <div
        className="fixed top-0 inset-x-0 h-44 pointer-events-none fade-top"
        style={{ zIndex: 5 }}
        aria-hidden="true"
      />

      {/* Top floating operational pill stack — replaces the legacy
          single Online pill. Three stacked pills (status / sector /
          today's progress) on the left, hamburger on the right. */}
      <TopOperationalPills
        isOnline={isOnline}
        onToggleOnline={onToggleOnline}
        sectorLabel={sectorLabel}
        ridesToday={sessionsForDate.length}
        completedToday={completedToday}
        onMenu={() => setMenuOpen(true)}
      />

      {/* Bottom sheet (gesture-driven) */}
      <DriverSheet
        selectedDate={selectedDate}
        today={today}
        canPrev={canPrev}
        canNext={canNext}
        onShiftDate={onShiftDate}
        onOpenCalendar={() => setCalendarOpen(true)}
        sessions={visibleSessions}
        activeSession={activeSession}
        activeLeg={activeLeg}
        activeEta={activeEta}
        hasGps={!!pos}
        onPickSession={(id) => setActiveSessionId(id)}
        now={now}
        actioning={actioning}
        onAdvance={advance}
        isOnline={isOnline}
        showCompleted={showCompleted}
        setShowCompleted={setShowCompleted}
        completedCount={completedCount}
        ridesToday={sessionsForDate.length}
      />

      {/* Bottom dock pill — rider-architecture sibling. MAP / BLOCKS /
          ACCOUNT segments, floating above the sheet at z-41 so it
          stays visible across sheet states without interfering with
          the drag handle (which lives at the top of the sheet). */}
      <DriverDock
        active={activeDock}
        onMap={() => { setActiveDock("map"); }}
        onBlocks={() => { setActiveDock("blocks"); }}
        onAccount={() => { setActiveDock("account"); setMenuOpen(true); }}
      />

      {/* Blocks placeholder sheet — opens when the BLOCKS dock segment
          is tapped. Real block-discovery API is queued for the next
          driver iteration; this surface communicates that and keeps
          the dock pill behavior consistent today. */}
      {activeDock === "blocks" && (
        <BlocksSheet onClose={() => setActiveDock("map")} />
      )}

      {/* Hamburger drawer */}
      <DriverMenu
        open={menuOpen}
        onClose={() => { setMenuOpen(false); if (activeDock === "account") setActiveDock("map"); }}
        user={user}
        isOnline={isOnline}
        onToggleOnline={onToggleOnline}
        onLogout={onLogout}
        onOpenSettings={() => { setMenuOpen(false); setSettingsOpen(true); }}
      />

      {/* Calendar modal */}
      {calendarOpen && (
        <CalendarModal
          dates={dates}
          today={today}
          selectedDate={selectedDate}
          allSessions={allSessions}
          onSelect={(d) => { setSelectedDate(d); setActiveSessionId(null); setCalendarOpen(false); }}
          onClose={() => setCalendarOpen(false)}
        />
      )}

      {/* Settings drawer (placeholder wired to existing theme) */}
      {settingsOpen && (
        <SettingsDrawer onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  );
}

/* ===========================================================
   DriverSheet — gesture-driven bottom sheet
   =========================================================== */
function DriverSheet({
  selectedDate, today, canPrev, canNext, onShiftDate, onOpenCalendar,
  sessions, activeSession, activeLeg, activeEta, hasGps,
  onPickSession, now, actioning, onAdvance, isOnline,
  showCompleted, setShowCompleted, completedCount, ridesToday,
}) {
  const [height, setHeight] = useState(SNAP_COLLAPSED_PX);
  const [snapping, setSnapping] = useState(true); // true = CSS transition on
  const [snaps, setSnaps] = useState({ collapsed: SNAP_COLLAPSED_PX, mid: 480, expanded: 720 });
  const dragRef = useRef({ active: false, startY: 0, startH: 0, lastY: 0, lastT: 0, vY: 0, pointerId: null });
  const sheetRef = useRef(null);

  // Recompute snap points on resize
  useEffect(() => {
    const recalc = () => setSnaps({
      collapsed: SNAP_COLLAPSED_PX,
      mid: getMidPx(),
      expanded: getExpandedPx(),
    });
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, []);

  // Keep current height clamped if snap points change
  useEffect(() => {
    setHeight((h) => clamp(h, snaps.collapsed, snaps.expanded));
  }, [snaps]);

  const snapTo = useCallback((target) => {
    setSnapping(true);
    setHeight(target);
  }, []);

  const onPointerDown = (e) => {
    // Only start drag from the handle area
    e.preventDefault();
    e.stopPropagation();
    try { e.target.setPointerCapture(e.pointerId); } catch {}
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startH: height,
      lastY: e.clientY,
      lastT: performance.now(),
      vY: 0,
      pointerId: e.pointerId,
    };
    setSnapping(false);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dy = d.startY - e.clientY;
    const newH = clamp(d.startH + dy, snaps.collapsed - 30, snaps.expanded + 30);
    setHeight(newH);
    const t = performance.now();
    const dt = t - d.lastT;
    if (dt > 0) {
      d.vY = (d.lastY - e.clientY) / dt; // px/ms; positive = upward
      d.lastY = e.clientY;
      d.lastT = t;
    }
  };

  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    d.active = false;
    try { e.target.releasePointerCapture?.(d.pointerId); } catch {}

    const v = d.vY;
    const cur = height;
    let target;
    if (Math.abs(v) > 0.5) {
      // velocity-driven
      if (v > 0) target = cur < snaps.mid - 20 ? snaps.mid : snaps.expanded;
      else       target = cur > snaps.mid + 20 ? snaps.mid : snaps.collapsed;
    } else {
      // nearest
      const list = [
        { h: snaps.collapsed, d: Math.abs(cur - snaps.collapsed) },
        { h: snaps.mid,       d: Math.abs(cur - snaps.mid) },
        { h: snaps.expanded,  d: Math.abs(cur - snaps.expanded) },
      ];
      list.sort((a, b) => a.d - b.d);
      target = list[0].h;
    }
    snapTo(target);
  };

  const sheetState =
    height <= snaps.collapsed + 30 ? "collapsed" :
    height >= snaps.expanded - 30 ? "expanded" :
    "mid";

  // Peek list for mid: first 2 upcoming sessions excluding active
  const peekSessions = useMemo(() => {
    if (!activeSession) return sessions.slice(0, 2);
    return sessions.filter((s) => s.id !== activeSession.id).slice(0, 2);
  }, [sessions, activeSession]);

  return (
    <div
      className="fixed inset-x-0 bottom-0 px-2 sm:px-3 pointer-events-none"
      style={{ zIndex: 40 }}
    >
      <div className="max-w-2xl mx-auto pointer-events-auto">
        <div
          ref={sheetRef}
          className="bg-[#0A1128]/95 backdrop-blur-2xl border border-white/10 border-b-0 rounded-t-[28px] shadow-[0_-20px_60px_-20px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col"
          style={{
            // Sheet extends BEHIND the iOS home-bar zone (Apple Maps style):
            // we render `height + safe-area-inset-bottom + dock allowance`
            // total, with a matching internal padding, so the visible
            // content area is always exactly `height` regardless of device
            // chrome.  The `+ 64px` dock allowance reserves space for the
            // bottom-floating dock pill (MAP / BLOCKS / ACCOUNT) so its
            // body never overlaps the sheet's content.
            height: `calc(${height}px + env(safe-area-inset-bottom, 0px) + 64px)`,
            transition: snapping ? "height 360ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
            willChange: "height",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)",
          }}
          data-testid="driver-sheet"
          data-sheet-state={sheetState}
        >
          {/* Drag handle — generous 72px touch zone (was ~20px, far too
              small for a thumb).  The visible pill stays compact at the
              top; the rest is a transparent reach zone. */}
          <div
            className="w-full flex flex-col items-center cursor-grab active:cursor-grabbing select-none"
            style={{
              touchAction: "none",
              minHeight: 72,
              paddingTop: 14,
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            data-testid="driver-sheet-handle"
          >
            <span className="block w-12 h-1.5 rounded-full bg-white/40" />
          </div>

          {/* Sheet header — date nav */}
          <SheetHeader
            selectedDate={selectedDate}
            today={today}
            canPrev={canPrev}
            canNext={canNext}
            onShiftDate={onShiftDate}
            onOpenCalendar={onOpenCalendar}
            ridesToday={ridesToday}
          />

          {/* Up-Next card (always visible — collapsed/mid/expanded) */}
          <UpNextCard
            session={activeSession}
            leg={activeLeg}
            eta={activeEta}
            hasGps={hasGps}
            now={now}
            actioning={actioning}
            onAdvance={onAdvance}
            isOnline={isOnline}
            ridesToday={ridesToday}
          />

          {/* Beyond-collapsed body */}
          {sheetState === "mid" && (
            <PeekList sessions={peekSessions} now={now} onPickSession={onPickSession} activeSessionId={activeSession?.id} />
          )}

          {sheetState === "expanded" && (
            <ExpandedList
              sessions={sessions}
              now={now}
              onPickSession={onPickSession}
              activeSessionId={activeSession?.id}
              showCompleted={showCompleted}
              setShowCompleted={setShowCompleted}
              completedCount={completedCount}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
   SheetHeader — date nav + ride count
   =========================================================== */
function SheetHeader({ selectedDate, today, canPrev, canNext, onShiftDate, onOpenCalendar, ridesToday }) {
  return (
    <div className="px-5 pt-1 pb-3 flex items-center justify-between gap-3" data-testid="sheet-header">
      <button
        type="button"
        onClick={() => onShiftDate(-1)}
        disabled={!canPrev}
        className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Previous day"
        data-testid="sheet-date-prev"
      >
        <ChevronLeft size={16} strokeWidth={1.8} />
      </button>
      <button
        type="button"
        onClick={onOpenCalendar}
        className="flex-1 min-w-0 text-center px-3 active:scale-[0.98] transition-transform"
        data-testid="sheet-date-label"
      >
        <div className="text-[9px] tracking-[0.3em] uppercase text-white/40">Schedule</div>
        <div className="font-serif text-[19px] truncate mt-0.5">{dateLongLabel(selectedDate, today)}</div>
        <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 mt-0.5">
          {ridesToday > 0 ? `${ridesToday} ride${ridesToday > 1 ? "s" : ""}` : "No rides"}
        </div>
      </button>
      <button
        type="button"
        onClick={() => onShiftDate(1)}
        disabled={!canNext}
        className="w-9 h-9 rounded-full bg-white/[0.05] border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Next day"
        data-testid="sheet-date-next"
      >
        <ChevronRight size={16} strokeWidth={1.8} />
      </button>
    </div>
  );
}

/* ===========================================================
   UpNextCard — single-card collapsed view
   =========================================================== */
function UpNextCard({ session, leg, eta, hasGps, now, actioning, onAdvance, isOnline, ridesToday }) {
  if (!session || !leg) {
    return (
      <div className="px-5 pb-5 pt-1" data-testid="upnext-empty">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/45 mb-1.5">Up Next</div>
        <div className="font-serif text-[22px] text-white leading-tight">
          {ridesToday > 0 ? "All caught up." : "No rides on the books."}
        </div>
        <div className="text-[12px] text-white/55 mt-1.5">
          {isOnline
            ? "We'll match you with new rides automatically."
            : "You're offline — go online to receive new rides."}
        </div>
      </div>
    );
  }

  const status = leg.status;
  const action = NEXT[status];
  const completed = status === "completed";
  const minsUntil = minutesUntil(leg.date, leg.pickup_time, now);
  const stage = ["arriving_soon", "in_progress"].includes(status) ? "destination" : "pickup";
  const targetAddr = stage === "destination" ? leg.dropoff_address : leg.pickup_address;
  const directionLabel = leg.direction === "return" ? "Return" : "Outbound";

  // Contextual eyebrow — shifts language to match the operational
  // moment the driver is in. Replaces the previous static "Up Next"
  // label for active states so the surface reads more like a live
  // ops console than a schedule item.
  const contextualLabel = (
    status === "driver_en_route"  ? "En route to pickup" :
    status === "arriving_soon"    ? "Waiting for rider" :
    status === "in_progress"      ? "Active leg" :
    "Up Next"
  );

  // Resolve ETA: live (from /driver/me/eta) > static fallback (route duration).
  const liveEtaMin = eta?.eta_minutes;
  const liveDistance = eta?.distance_miles;
  const staticEtaMin = leg.duration_minutes;
  const showLive = hasGps && liveEtaMin != null;
  const etaMinutes = showLive ? liveEtaMin : staticEtaMin;
  const etaMiles = showLive ? liveDistance : leg.distance_miles;
  const etaSource = eta?.source;

  return (
    <div className="px-5 pb-5 pt-1" data-testid="upnext-card">
      {/* Top header row — contextual eyebrow shifts label to "EN ROUTE",
          "WAITING", or "ACTIVE LEG" once the driver is mid-flow. */}
      <div className="flex items-center justify-between gap-3 mb-2.5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#ed6500]" data-testid="upnext-eyebrow">
          {contextualLabel}
        </div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 inline-flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
          {STATUS_LABEL[status]}
          {minsUntil != null && minsUntil > 0 && minsUntil <= 60 && (
            <span className="text-[#ed6500] ml-1" data-testid="upnext-countdown">· {relativeTimeLabel(minsUntil)}</span>
          )}
        </div>
      </div>

      {/* Rider name + direction + time */}
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="font-serif text-[24px] truncate" data-testid="upnext-rider">
          {session.rider_name || "Rider"}
        </div>
        <div className="text-[12px] tracking-[0.18em] uppercase text-white/55 shrink-0" data-testid="upnext-time">
          {formatTime12(leg.pickup_time) || "—"}
        </div>
      </div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/40 mb-3">
        {directionLabel} · {stage === "destination" ? "to drop-off" : "to pickup"}
      </div>

      {/* Address (tappable → Google Maps) */}
      <a
        href={googleMapsDeeplink(targetAddr)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { if (!targetAddr) e.preventDefault(); }}
        className="group block bg-white/[0.04] hover:bg-white/[0.08] border border-white/8 rounded-2xl px-4 py-3 mb-4 transition-colors active:scale-[0.99]"
        data-testid="upnext-address"
      >
        <div className="flex items-start gap-3">
          <MapPin size={16} strokeWidth={1.7} className="text-[#ed6500] mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="text-[9px] tracking-[0.28em] uppercase text-white/40">{stage === "destination" ? "Drop-off" : "Pickup"}</div>
              {etaMinutes != null && !completed && (
                <div
                  className={[
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase",
                    showLive
                      ? "bg-[#ed6500]/18 text-[#ed6500] border border-[#ed6500]/30"
                      : "bg-white/[0.06] text-white/55 border border-white/10",
                  ].join(" ")}
                  title={showLive ? "Live ETA from your location" : "Estimated route ETA"}
                  data-testid="upnext-eta-badge"
                  data-eta-source={etaSource || (showLive ? "live" : "static_fallback")}
                >
                  {showLive && <span className="w-1 h-1 rounded-full bg-[#ed6500] animate-pulse" />}
                  <span>{etaMinutes} min</span>
                  {etaMiles != null && <span className="opacity-60">· {Number(etaMiles).toFixed(1)} mi</span>}
                </div>
              )}
            </div>
            <div className="text-[14px] text-white/90 leading-snug">{targetAddr || "—"}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-1.5 inline-flex items-center gap-1.5 group-hover:text-[#ed6500]">
              <Navigation size={10} strokeWidth={1.8} /> Open in Google Maps
            </div>
          </div>
        </div>
      </a>

      {/* Primary action */}
      {!completed && action && (
        <button
          type="button"
          onClick={onAdvance}
          disabled={actioning}
          className="w-full h-13 py-3.5 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.32em] uppercase font-medium disabled:opacity-50 transition-all active:scale-[0.99] inline-flex items-center justify-center gap-2"
          data-testid="upnext-action"
        >
          {actioning ? (
            <><Loader2 size={14} className="animate-spin" /> Updating</>
          ) : (
            <><Navigation size={14} strokeWidth={1.8} /> {action.label}</>
          )}
        </button>
      )}
      {completed && (
        <div className="w-full h-13 py-3.5 rounded-2xl border border-emerald-400/40 bg-emerald-400/10 inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.28em] uppercase text-emerald-300" data-testid="upnext-done">
          <CheckCircle2 size={15} strokeWidth={1.8} /> Trip completed
        </div>
      )}

      {/* Quick actions (call/text rider) */}
      {session.rider_phone && (
        <div className="grid grid-cols-2 gap-2 mt-3" data-testid="upnext-quick-actions">
          <a
            href={`tel:${session.rider_phone}`}
            className="h-11 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 inline-flex items-center justify-center gap-2 text-[10px] tracking-[0.26em] uppercase text-white/85 transition-colors"
            data-testid="upnext-call"
          >
            <Phone size={12} strokeWidth={1.8} /> Call
          </a>
          <a
            href={`sms:${session.rider_phone}`}
            className="h-11 rounded-xl bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 inline-flex items-center justify-center gap-2 text-[10px] tracking-[0.26em] uppercase text-white/85 transition-colors"
            data-testid="upnext-text"
          >
            <MessageSquare size={12} strokeWidth={1.8} /> Text
          </a>
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   PeekList — mid-state: shows next 2 upcoming
   =========================================================== */
function PeekList({ sessions, now, onPickSession, activeSessionId }) {
  if (sessions.length === 0) {
    return (
      <div className="px-5 pb-5 text-center text-[12px] text-white/45 pt-2" data-testid="peek-empty">
        No other rides today
      </div>
    );
  }
  return (
    <div className="px-5 pb-5 space-y-2 pt-1" data-testid="peek-list">
      <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1.5">Coming Up</div>
      {sessions.map((s) => (
        <SessionCardCompact
          key={s.id}
          session={s}
          now={now}
          isActive={s.id === activeSessionId}
          onPick={() => onPickSession(s.id)}
        />
      ))}
    </div>
  );
}

/* ===========================================================
   ExpandedList — full grouped session list
   =========================================================== */
function ExpandedList({ sessions, now, onPickSession, activeSessionId, showCompleted, setShowCompleted, completedCount }) {
  if (sessions.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-5 pb-8" data-testid="expanded-empty">
        <Calendar size={26} strokeWidth={1.4} className="text-white/25 mb-3" />
        <div className="font-serif text-[16px] text-white/75 mb-1">No rides scheduled</div>
        <div className="text-[12px] text-white/45 text-center">
          {completedCount > 0 ? `${completedCount} completed today — toggle below to view.` : "Tap the date arrows to view another day."}
        </div>
        {completedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowCompleted(!showCompleted)}
            className="mt-4 text-[10px] tracking-[0.28em] uppercase text-[#ed6500] active:scale-95"
            data-testid="show-completed-toggle"
          >
            {showCompleted ? "Hide completed" : `Show ${completedCount} completed`}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-y-auto px-4 pb-5 pt-1" style={{ WebkitOverflowScrolling: "touch" }} data-testid="expanded-list">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[9px] tracking-[0.3em] uppercase text-white/45">All Rides</div>
        {completedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowCompleted(!showCompleted)}
            className="text-[9px] tracking-[0.26em] uppercase text-[#ed6500] inline-flex items-center gap-1.5 active:scale-95"
            data-testid="show-completed-toggle"
          >
            {showCompleted ? <EyeOff size={11} strokeWidth={1.8} /> : <Eye size={11} strokeWidth={1.8} />}
            {showCompleted ? "Hide completed" : `Show ${completedCount} done`}
          </button>
        )}
      </div>
      <ul className="space-y-2.5">
        {sessions.map((s, idx) => (
          <SessionCard
            key={s.id}
            session={s}
            idx={idx}
            isActive={activeSessionId === s.id}
            onPick={() => onPickSession(s.id)}
            now={now}
          />
        ))}
      </ul>
    </div>
  );
}

/* ===========================================================
   SessionCardCompact — peek view
   =========================================================== */
function SessionCardCompact({ session, now, isActive, onPick }) {
  const ob = session.outbound;
  const rb = session.return;
  const upcoming = [ob, rb].filter(Boolean).find((l) => !["completed", "cancelled"].includes(l.status))
                || ob || rb;
  const mins = upcoming ? minutesUntil(upcoming.date, upcoming.pickup_time, now) : null;
  const completed = [ob, rb].filter(Boolean).every((l) => l.status === "completed");

  return (
    <button
      type="button"
      onClick={onPick}
      className={[
        "w-full text-left px-3.5 py-3 rounded-2xl border flex items-center justify-between gap-3 transition-all active:scale-[0.99]",
        isActive ? "bg-white/[0.07] border-[#ed6500]/60" : "bg-white/[0.02] border-white/8 hover:bg-white/[0.05]",
        completed ? "opacity-50" : "",
      ].join(" ")}
      data-testid="peek-session"
    >
      <div className="min-w-0">
        <div className="text-[14px] font-medium truncate">{session.rider_name}</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mt-0.5">
          {formatTime12(upcoming?.pickup_time) || "—"}
          {ob && rb && <span className="ml-2">· Round trip</span>}
        </div>
      </div>
      {mins != null && mins > 0 && mins <= 90 && (
        <span className="text-[10px] tracking-[0.22em] uppercase text-[#ed6500] shrink-0">
          {relativeTimeLabel(mins)}
        </span>
      )}
    </button>
  );
}

/* ===========================================================
   SessionCard — full grouped card (expanded view)
   =========================================================== */
function SessionCard({ session, idx, isActive, onPick, now }) {
  const { rider_name, rider_phone, outbound, return: rb, tier_name, distance_miles } = session;
  const legs = [outbound, rb].filter(Boolean);
  const allDone = legs.length > 0 && legs.every((l) => l.status === "completed");
  const anyLive = legs.some((l) => ACTIVE_STATES.has(l.status));
  const statusKey = anyLive ? legs.find((l) => ACTIVE_STATES.has(l.status)).status
                  : allDone ? "completed"
                  : (legs[0]?.status || "scheduled");
  const upcomingLeg = legs.find((l) => !["completed", "cancelled"].includes(l.status));
  const mins = upcomingLeg ? minutesUntil(upcomingLeg.date, upcomingLeg.pickup_time, now) : null;

  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        className={[
          "w-full text-left rounded-2xl border p-4 transition-all active:scale-[0.99]",
          isActive
            ? "bg-white/[0.07] border-[#ed6500]/60 shadow-[0_0_0_1px_rgba(237,101,0,0.3)]"
            : "bg-white/[0.03] border-white/8 hover:bg-white/[0.05] hover:border-white/15",
          allDone ? "opacity-55" : "",
        ].join(" ")}
        data-testid={`expanded-session-${idx}`}
        data-active={isActive}
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="min-w-0 flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[statusKey] || "bg-white/30"}`} />
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              {STATUS_LABEL[statusKey] || statusKey}
            </span>
            {tier_name && (
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/35">· {tier_name}</span>
            )}
          </div>
          {mins != null && mins > 0 && mins <= 90 && (
            <span className="text-[10px] tracking-[0.22em] uppercase text-[#ed6500] shrink-0">
              {relativeTimeLabel(mins)}
            </span>
          )}
        </div>
        <div className="font-serif text-[18px] truncate mb-2.5 text-white">{rider_name}</div>
        <div className="space-y-1.5 text-[12px]">
          {outbound && <LegLine leg={outbound} label="Outbound" />}
          {rb && <LegLine leg={rb} label="Return" />}
        </div>
        {(rider_phone || distance_miles) && (
          <div className="mt-3 pt-3 border-t border-white/8 flex items-center justify-between gap-2 text-[10px] text-white/45 tracking-[0.2em] uppercase">
            <span>{distance_miles ? `${Number(distance_miles).toFixed(1)} mi` : ""}</span>
            {rider_phone && (
              <span className="inline-flex items-center gap-1.5">
                <Phone size={11} strokeWidth={1.7} /> {rider_phone}
              </span>
            )}
          </div>
        )}
      </button>
    </li>
  );
}

function LegLine({ leg, label }) {
  const isLive = ACTIVE_STATES.has(leg.status);
  const isDone = leg.status === "completed";
  const isCancelled = leg.status === "cancelled";
  const dur = leg.duration_minutes;
  const dist = leg.distance_miles;
  return (
    <div className="flex items-start gap-2.5">
      <div className="shrink-0 w-12 pt-0.5">
        <div className={`text-[10px] tracking-[0.2em] uppercase ${isLive ? "text-[#ed6500]" : "text-white/45"}`}>{label}</div>
        <div className={`text-[13px] font-medium ${isDone ? "text-white/40 line-through" : isCancelled ? "text-white/30 line-through" : "text-white/85"}`}>
          {formatTime12(leg.pickup_time) || "—"}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 truncate text-white/65">
          <span className="w-1 h-1 rounded-full bg-[#5c97f7] shrink-0" />
          <span className="truncate">{leg.pickup_address}</span>
        </div>
        <div className="flex items-center gap-1.5 truncate text-white/65 mt-0.5">
          <span className="w-1 h-1 rounded-full bg-[#ed6500] shrink-0" />
          <span className="truncate">{leg.dropoff_address}</span>
        </div>
        {!isDone && !isCancelled && (dur || dist) && (
          <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mt-1 inline-flex items-center gap-1.5" data-testid={`leg-eta-${leg.direction}`}>
            {dur && <span>{dur} min</span>}
            {dur && dist && <span>·</span>}
            {dist && <span>{Number(dist).toFixed(1)} mi</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===========================================================
   DriverMenu — slide-out drawer
   =========================================================== */
function DriverMenu({ open, onClose, user, isOnline, onToggleOnline, onLogout, onOpenSettings }) {
  return (
    <SidePanel
      open={open}
      onClose={onClose}
      side="right"
      testid="driver-menu-panel"
      ariaLabel="Driver menu"
      className="bg-[#0A1128] border-l border-white/8 flex flex-col"
    >
      <div style={{ paddingTop: "max(env(safe-area-inset-top), 16px)" }} className="flex flex-col h-full">
        <div className="px-5 pt-2 pb-5 flex items-center justify-between border-b border-white/8">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-1">Driver</div>
            <div className="font-serif text-[22px] text-white truncate">{user?.name || "Driver"}</div>
            <div className="text-[12px] text-white/55 truncate mt-0.5">{user?.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/10 flex items-center justify-center link-pressable"
            aria-label="Close"
            data-testid="driver-menu-close"
          >
            <X size={16} strokeWidth={1.7} className="text-white/70" />
          </button>
        </div>

        <nav className="py-2 flex-1">
          <MenuItem
            icon={<User size={16} strokeWidth={1.7} />}
            label="Profile"
            badge="Coming soon"
            disabled
            testid="menu-profile"
          />
          <MenuItemToggle
            icon={isOnline ? <Circle size={14} fill="#10b981" strokeWidth={0} /> : <Circle size={14} fill="#6b7280" strokeWidth={0} />}
            label="Online"
            description={isOnline ? "Accepting new rides" : "Paused — no new rides"}
            checked={isOnline}
            onToggle={onToggleOnline}
            testid="menu-online-toggle"
          />
          <MenuItem
            icon={<Compass size={16} strokeWidth={1.7} />}
            label="Find Blocks"
            badge="Coming soon"
            disabled
            testid="menu-find-blocks"
          />
          <MenuItem
            icon={<SettingsIcon size={16} strokeWidth={1.7} />}
            label="Settings"
            onClick={onOpenSettings}
            testid="menu-settings"
          />
        </nav>

        <button
          type="button"
          onClick={onLogout}
          className="mx-5 mb-6 h-12 rounded-2xl border border-white/10 hover:border-white/25 inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/85 link-pressable"
          style={{ marginBottom: "max(env(safe-area-inset-bottom, 0px), 24px)" }}
          data-testid="driver-menu-signout"
        >
          <Power size={14} strokeWidth={1.7} /> Sign Out
        </button>
      </div>
    </SidePanel>
  );
}

function MenuItem({ icon, label, onClick, disabled, badge, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full px-5 py-3.5 flex items-center gap-3 hover:bg-white/[0.04] active:bg-white/[0.06] disabled:opacity-40 disabled:cursor-not-allowed text-left transition-colors"
      data-testid={testid}
    >
      <span className="w-7 h-7 rounded-full bg-white/[0.06] flex items-center justify-center text-white/85 shrink-0">{icon}</span>
      <span className="flex-1 text-[14px] text-white/90">{label}</span>
      {badge && (
        <span className="text-[9px] tracking-[0.22em] uppercase text-white/45 inline-flex items-center gap-1.5">
          <Lock size={10} strokeWidth={1.8} /> {badge}
        </span>
      )}
    </button>
  );
}

function MenuItemToggle({ icon, label, description, checked, onToggle, testid }) {
  return (
    <div className="w-full px-5 py-3.5 flex items-center gap-3">
      <span className="w-7 h-7 rounded-full bg-white/[0.06] flex items-center justify-center text-white/85 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] text-white/90">{label}</div>
        <div className="text-[11px] text-white/50 truncate">{description}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        className={[
          "shrink-0 w-12 h-7 rounded-full relative transition-colors",
          checked ? "bg-emerald-500" : "bg-white/15",
        ].join(" ")}
        data-testid={testid}
      >
        <span
          className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all"
          style={{ left: checked ? "22px" : "2px" }}
        />
      </button>
    </div>
  );
}

/* ===========================================================
   CalendarModal — full-screen date picker
   =========================================================== */
function CalendarModal({ dates, today, selectedDate, allSessions, onSelect, onClose }) {
  const countByDate = useMemo(() => {
    const map = {};
    for (const s of allSessions) {
      const d = s.outbound?.date || s.return?.date || s.date;
      map[d] = (map[d] || 0) + 1;
    }
    return map;
  }, [allSessions]);

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 70 }}
        onClick={onClose}
        aria-label="Close calendar"
        data-testid="calendar-scrim"
      />
      <div
        className="fixed inset-x-0 bottom-0 bg-[#0A1128] border-t border-white/10 rounded-t-[28px] flex flex-col"
        style={{ zIndex: 71, maxHeight: "85dvh", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="calendar-modal"
      >
        <div className="w-full pt-3 pb-2 flex justify-center">
          <span className="block w-10 h-1 rounded-full bg-white/35" />
        </div>
        <div className="px-5 pb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-1">Schedule</div>
            <div className="font-serif text-[22px]">Pick a day</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
            data-testid="calendar-close"
          >
            <X size={16} strokeWidth={1.7} className="text-white/70" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-6">
          <ul>
            {dates.map((d) => {
              const isToday = d === today;
              const isSelected = d === selectedDate;
              const count = countByDate[d] || 0;
              const [y, m, dd] = d.split("-").map(Number);
              const dt = new Date(y, m - 1, dd);
              const weekday = dt.toLocaleDateString(undefined, { weekday: "long" });
              const monthDay = dt.toLocaleDateString(undefined, { month: "long", day: "numeric" });
              return (
                <li key={d}>
                  <button
                    type="button"
                    onClick={() => onSelect(d)}
                    className={[
                      "w-full px-4 py-4 flex items-center justify-between gap-3 rounded-2xl transition-all active:scale-[0.99] mb-1.5",
                      isSelected ? "bg-[#ed6500] text-white" : "hover:bg-white/[0.04] text-white/90",
                    ].join(" ")}
                    data-testid={`calendar-day-${d}`}
                  >
                    <div className="text-left">
                      <div className={`text-[10px] tracking-[0.22em] uppercase mb-0.5 ${isSelected ? "text-white/85" : isToday ? "text-[#ed6500]" : "text-white/45"}`}>
                        {isToday ? "Today" : weekday}
                      </div>
                      <div className="font-serif text-[18px]">{monthDay}</div>
                    </div>
                    <div className={`text-[10px] tracking-[0.18em] uppercase ${isSelected ? "text-white/85" : "text-white/45"}`}>
                      {count > 0 ? `${count} ride${count > 1 ? "s" : ""}` : "Open"}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
}

/* ===========================================================
   SettingsDrawer — wires existing theme + notification togglees
   =========================================================== */
function SettingsDrawer({ onClose }) {
  const { preference, setPreference } = useTheme();
  const themes = [
    { id: "system", label: "System", icon: <Monitor size={14} strokeWidth={1.8} /> },
    { id: "light",  label: "Light",  icon: <Sun     size={14} strokeWidth={1.8} /> },
    { id: "dark",   label: "Dark",   icon: <Moon    size={14} strokeWidth={1.8} /> },
  ];
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 70 }}
        onClick={onClose}
        aria-label="Close settings"
        data-testid="settings-scrim"
      />
      <div
        className="fixed inset-x-0 bottom-0 bg-[#0A1128] border-t border-white/10 rounded-t-[28px] flex flex-col"
        style={{ zIndex: 71, maxHeight: "70dvh", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="settings-drawer"
      >
        <div className="w-full pt-3 pb-2 flex justify-center">
          <span className="block w-10 h-1 rounded-full bg-white/35" />
        </div>
        <div className="px-5 pb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-1">Settings</div>
            <div className="font-serif text-[22px]">Appearance</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
            data-testid="settings-close"
          >
            <X size={16} strokeWidth={1.7} className="text-white/70" />
          </button>
        </div>
        <div className="px-5 pb-6">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2">Theme</div>
          <div className="grid grid-cols-3 gap-2">
            {themes.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setPreference(t.id)}
                className={[
                  "h-12 rounded-2xl border inline-flex items-center justify-center gap-2 transition-all",
                  preference === t.id
                    ? "bg-[#ed6500] border-[#ed6500] text-white"
                    : "bg-white/[0.04] border-white/10 text-white/85 hover:bg-white/[0.08]",
                ].join(" ")}
                data-testid={`settings-theme-${t.id}`}
              >
                {t.icon}
                <span className="text-[10px] tracking-[0.24em] uppercase">{t.label}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-white/45 mt-4 leading-relaxed">
            More settings coming soon — we're starting with the essentials.
          </p>
        </div>
      </div>
    </>
  );
}

/* ===========================================================
   TopOperationalPills — floating pill stack at the top-left.
   Three pills:  status (clickable toggle), sector, today's progress.
   Designed to mirror the rider app's pill aesthetic (navy glass,
   rounded, layered) without competing with the bottom sheet.
   =========================================================== */
function TopOperationalPills({ isOnline, onToggleOnline, sectorLabel, ridesToday, completedToday, onMenu }) {
  // Pulse the progress pill briefly whenever a ride is newly completed.
  const lastDoneRef = useRef(completedToday);
  const [bump, setBump] = useState(false);
  useEffect(() => {
    if (completedToday > lastDoneRef.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 1200);
      lastDoneRef.current = completedToday;
      return () => clearTimeout(t);
    }
    lastDoneRef.current = completedToday;
  }, [completedToday]);

  return (
    <div
      className="fixed top-0 inset-x-0 px-3 sm:px-4 pointer-events-none flex items-start justify-between gap-2"
      style={{ zIndex: 30, paddingTop: "max(env(safe-area-inset-top), 14px)" }}
      data-testid="driver-top-chrome"
    >
      <div className="flex flex-col items-start gap-2 pointer-events-none">
        {/* Status pill — click to toggle online */}
        <button
          type="button"
          onClick={onToggleOnline}
          className="inline-flex items-center gap-2 bg-[#0A1128]/85 hover:bg-[#0A1128]/95 backdrop-blur-xl border border-white/10 rounded-full px-3.5 py-2 text-[10px] tracking-[0.28em] uppercase shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] pointer-events-auto active:scale-[0.97] transition-all driver-pill-enter"
          style={{ animationDelay: "0ms" }}
          data-testid="driver-online-pill"
          aria-label={isOnline ? "Tap to go offline" : "Tap to go online"}
        >
          <Circle
            size={8}
            fill={isOnline ? "#10b981" : "#6b7280"}
            strokeWidth={0}
            className={isOnline ? "animate-pulse" : ""}
          />
          <span className={isOnline ? "text-emerald-300" : "text-white/55"}>
            {isOnline ? "Online" : "Offline"}
          </span>
        </button>

        {/* Sector pill — current operating zone */}
        <div
          className="inline-flex items-center gap-2 bg-[#0A1128]/75 backdrop-blur-xl border border-white/10 rounded-full px-3.5 py-2 text-[10px] tracking-[0.22em] uppercase shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)] pointer-events-auto driver-pill-enter"
          style={{ animationDelay: "80ms" }}
          data-testid="driver-sector-pill"
        >
          <Compass size={11} strokeWidth={1.8} className="text-[#5c97f7]" />
          <span className="text-white/80">{sectorLabel}</span>
        </div>

        {/* Today's progress pill */}
        {ridesToday > 0 && (
          <div
            className={[
              "inline-flex items-center gap-2 bg-[#0A1128]/75 backdrop-blur-xl border rounded-full px-3.5 py-2 text-[10px] tracking-[0.22em] uppercase shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)] pointer-events-auto driver-pill-enter transition-all",
              bump ? "border-[#ed6500]/80 scale-[1.04] shadow-[0_0_0_4px_rgba(237,101,0,0.18)]" : "border-white/10",
            ].join(" ")}
            style={{ animationDelay: "160ms" }}
            data-testid="driver-progress-pill"
          >
            <TrendingUp size={11} strokeWidth={1.8} className={bump ? "text-[#ed6500]" : "text-[#ed6500]/80"} />
            <span className={bump ? "text-[#ed6500]" : "text-white/80"}>
              {completedToday}/{ridesToday} <span className="opacity-65">today</span>
            </span>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onMenu}
        className="w-10 h-10 rounded-full bg-[#0A1128]/85 backdrop-blur-xl border border-white/10 flex items-center justify-center active:scale-95 transition-transform shadow-lg pointer-events-auto driver-pill-enter"
        aria-label="Open menu"
        data-testid="driver-menu-btn"
      >
        <Menu size={16} strokeWidth={1.7} />
      </button>
    </div>
  );
}

/* ===========================================================
   SectorOverlay — soft decorative pills floating across the map
   showing the platform's operational zones. No demand API yet, so
   intensity cycles by time of day (rush hour = warm orange, off
   hours = cool blue) to give the surface that "alive city" feel.
   =========================================================== */
const SECTOR_OVERLAYS = [
  { id: "downtown",     name: "Downtown",     top: "26%", left: "44%" },
  { id: "plaza",        name: "Plaza",        top: "52%", left: "38%" },
  { id: "north-kc",     name: "North KC",     top: "16%", left: "56%" },
  { id: "westport",     name: "Westport",     top: "40%", left: "30%" },
  { id: "independence", name: "Independence", top: "32%", left: "72%" },
];
function SectorOverlay({ now }) {
  // Rush-hour intensity: warm 7-9a + 4-7p, cooler otherwise.
  const hour = useMemo(() => new Date(now).getHours(), [now]);
  const intensity = (hour >= 7 && hour <= 9) || (hour >= 16 && hour <= 19) ? "high" : "normal";

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 4 }} aria-hidden="true" data-testid="sector-overlay">
      {SECTOR_OVERLAYS.map((s, i) => {
        const isHot = intensity === "high" && i % 2 === 0; // alternate hot zones during rush
        return (
          <div
            key={s.id}
            className="absolute -translate-x-1/2 -translate-y-1/2 driver-pill-enter"
            style={{ top: s.top, left: s.left, animationDelay: `${260 + i * 80}ms` }}
          >
            <div
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9px] tracking-[0.26em] uppercase font-medium backdrop-blur-md border shadow-sm",
                isHot
                  ? "bg-[#ed6500]/22 border-[#ed6500]/45 text-[#ff8a36]"
                  : "bg-[#5c97f7]/18 border-[#5c97f7]/35 text-[#a4c4f7]",
              ].join(" ")}
              data-testid={`sector-chip-${s.id}`}
            >
              <span className={`w-1 h-1 rounded-full ${isHot ? "bg-[#ed6500] animate-pulse" : "bg-[#5c97f7]"}`} />
              {s.name}
            </div>
          </div>
        );
      })}
    </div>
  );
}


/* ===========================================================
   DriverDock — bottom-center floating pill row, sibling to the
   rider app's MobileTabBar. Three segments (MAP / BLOCKS /
   ACCOUNT), active segment fills orange, inactive stays navy
   glass. Positioned at z-41 so it floats above the sheet
   without competing with the sheet's drag handle (which lives
   at the top of the sheet).
   =========================================================== */
function DriverDock({ active, onMap, onBlocks, onAccount }) {
  return (
    <div
      className="fixed inset-x-0 px-3 pointer-events-none flex justify-center driver-pill-enter"
      style={{
        zIndex: 41,
        bottom: "max(env(safe-area-inset-bottom, 0px), 12px)",
        animationDelay: "240ms",
      }}
      data-testid="driver-dock"
    >
      <div
        className="pointer-events-auto inline-flex items-center gap-1 bg-[#0A1128]/92 backdrop-blur-xl border border-white/10 rounded-full p-1 shadow-[0_18px_36px_-12px_rgba(0,0,0,0.75)]"
        role="tablist"
        aria-label="Driver navigation"
      >
        <DockSegment
          icon={<MapIcon size={13} strokeWidth={1.9} />}
          label="Map"
          isActive={active === "map"}
          onClick={onMap}
          testId="driver-dock-map"
        />
        <DockSegment
          icon={<Layers size={13} strokeWidth={1.9} />}
          label="Blocks"
          isActive={active === "blocks"}
          onClick={onBlocks}
          testId="driver-dock-blocks"
        />
        <DockSegment
          icon={<User size={13} strokeWidth={1.9} />}
          label="Account"
          isActive={active === "account"}
          onClick={onAccount}
          testId="driver-dock-account"
        />
      </div>
    </div>
  );
}

function DockSegment({ icon, label, isActive, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={isActive}
      className={[
        "h-10 px-4 rounded-full inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold transition-all active:scale-[0.97]",
        isActive
          ? "bg-[#ed6500] text-white shadow-[0_6px_20px_-6px_rgba(237,101,0,0.6)]"
          : "text-white/65 hover:text-white hover:bg-white/[0.06]",
      ].join(" ")}
      data-testid={testId}
    >
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

/* ===========================================================
   BlocksSheet — placeholder discovery surface while the real
   /api/driver/blocks endpoint is being wired up. Matches the
   calendar/settings modal language (dark navy, drag handle,
   rounded top, safe-area aware) so it feels native.
   =========================================================== */
function BlocksSheet({ onClose }) {
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        style={{ zIndex: 70 }}
        onClick={onClose}
        aria-label="Close blocks"
        data-testid="blocks-scrim"
      />
      <div
        className="fixed inset-x-0 bottom-0 bg-[#0A1128] border-t border-white/10 rounded-t-[28px] flex flex-col"
        style={{ zIndex: 71, maxHeight: "75dvh", paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="blocks-sheet"
      >
        <div className="w-full pt-3 pb-2 flex justify-center">
          <span className="block w-10 h-1 rounded-full bg-white/35" />
        </div>
        <div className="px-5 pb-3 flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#ed6500] mb-1">Discover</div>
            <div className="font-serif text-[24px] leading-tight">Blocks</div>
            <div className="text-[12px] text-white/55 mt-1.5 max-w-prose">
              Claim a window of guaranteed rides. Pick a sector, lock the hours, ride the demand.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
            data-testid="blocks-close"
          >
            <X size={16} strokeWidth={1.7} className="text-white/70" />
          </button>
        </div>

        <div className="px-5 pb-6 pt-2">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-6 text-center" data-testid="blocks-empty">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#ed6500]/15 border border-[#ed6500]/30 flex items-center justify-center mb-3">
              <Clock size={18} strokeWidth={1.6} className="text-[#ed6500]" />
            </div>
            <div className="font-serif text-[18px] mb-1.5">Coming soon</div>
            <p className="text-[12px] text-white/55 leading-relaxed">
              Block discovery is being wired to live demand data. You'll see open windows by sector, estimated earnings, and a one-tap claim — right here.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 h-11 px-5 inline-flex items-center gap-2 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[10.5px] tracking-[0.26em] uppercase font-semibold transition-colors active:scale-[0.98]"
              data-testid="blocks-back-to-map"
            >
              <MapIcon size={13} strokeWidth={1.9} />
              Back to map
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
